// ============================================================================
// UNIFIED REPORT ENGINE — SECTION REGISTRY
// ============================================================================
// Default section templates for every document type. When a user creates a
// new template from scratch, these defaults populate the sections array.
// ============================================================================

import {
  type DocumentType,
  type UnifiedTemplateSection,
  type TemplateTableColumn,
  DOCUMENT_TYPE_CATEGORY,
  DOCUMENT_TYPE_LABELS,
  DEFAULT_LINE_ITEM_COLUMNS,
  DELIVERY_NOTE_COLUMNS,
  WAREHOUSE_MOVEMENT_COLUMNS,
} from './types';

// ============================================================================
// SECTION FACTORY HELPERS
// ============================================================================

let sectionIdCounter = 0;

/** Generate a unique section ID */
function sid(): string {
  sectionIdCounter += 1;
  return `section_${Date.now()}_${sectionIdCounter}`;
}

/** Reset counter (useful for tests) */
export function resetSectionIdCounter(): void {
  sectionIdCounter = 0;
}

function section(type: UnifiedTemplateSection['type'], label: string, config: UnifiedTemplateSection['config'] = {}): UnifiedTemplateSection {
  return { id: sid(), type, label, enabled: true, config };
}

export interface SectionSourceOption {
  key: string;
  label: string;
  description?: string;
  enabledByDefault?: boolean;
}

export interface SectionFieldOption {
  key: string;
  label: string;
  description?: string;
}

type OperationsDocumentType = 'job_card' | 'attendance_card' | 'task_card' | 'service_report' | 'work_order' | 'vehicle_inspection_report';

function isOperationsDocumentType(docType: DocumentType): docType is OperationsDocumentType {
  return ['job_card', 'attendance_card', 'task_card', 'service_report', 'work_order', 'vehicle_inspection_report'].includes(docType);
}

const OPERATION_SECTION_SOURCE_LIBRARY = {
  overview: {
    key: 'overview',
    label: 'Work Summary',
    description: 'Title, reference, status, priority, dates, and description.',
    enabledByDefault: true,
  },
  client_contact: {
    key: 'client_contact',
    label: 'Client & Location',
    description: 'Customer contact details and site/location information.',
    enabledByDefault: false,
  },
  personnel: {
    key: 'personnel',
    label: 'Assigned Personnel',
    description: 'Assigned technician, employee, or team members.',
    enabledByDefault: true,
  },
  attendance_timeline: {
    key: 'attendance_timeline',
    label: 'Timeline & Attendance',
    description: 'Travel, check-in, work start/end, and duration timeline. Section config: showExactTimes shows clock times on instants; showTravelDetails / showWorkDetails / showLifecycleMilestones toggle groups.',
    enabledByDefault: true,
  },
  subtasks: {
    key: 'subtasks',
    label: 'Subtasks',
    description: 'Linked subtasks and child task progress.',
    enabledByDefault: false,
  },
  billing_summary: {
    key: 'billing_summary',
    label: 'Billing Summary',
    description: 'Calculated labour and material totals.',
    enabledByDefault: false,
  },
  expenses: {
    key: 'expenses',
    label: 'Expenses',
    description: 'Linked expenses captured against the work item.',
    enabledByDefault: false,
  },
  closing_details: {
    key: 'closing_details',
    label: 'Closing details',
    description: 'Sign-off / closing notes (default type for customer-facing comments that are not marked external).',
    enabledByDefault: false,
  },
  external_comments: {
    key: 'external_comments',
    label: 'External comments',
    description: 'Comments explicitly marked as external / customer-visible.',
    enabledByDefault: false,
  },
  internal_comments: {
    key: 'internal_comments',
    label: 'Internal comments',
    description: 'Team-only internal notes (hidden from typical customer views unless you enable this block).',
    enabledByDefault: false,
  },
  attachments: {
    key: 'attachments',
    label: 'Attachments',
    description: 'Referenced supporting files and documents.',
    enabledByDefault: false,
  },
  additional_info: {
    key: 'additional_info',
    label: 'Additional Information',
    description: 'Notes, safety requirements, and equipment requirements.',
    enabledByDefault: false,
  },
  vehicle_details: {
    key: 'vehicle_details',
    label: 'Vehicle Details',
    description: 'Vehicle registration, asset details, and assigned driver information.',
    enabledByDefault: true,
  },
  inspection_capture: {
    key: 'inspection_capture',
    label: 'Inspection Capture',
    description: 'Inspector details, odometer, fuel level, and capture location.',
    enabledByDefault: true,
  },
  issues_summary: {
    key: 'issues_summary',
    label: 'Issues & Follow-up',
    description: 'Detected issues, review status, and follow-up requirements.',
    enabledByDefault: true,
  },
  ai_review: {
    key: 'ai_review',
    label: 'AI Review Summary',
    description: 'AI findings, risk assessment, and recommended actions.',
    enabledByDefault: true,
  },
  notes: {
    key: 'notes',
    label: 'Inspector Notes',
    description: 'Inspector notes, checklist summary, and capture context.',
    enabledByDefault: true,
  },
} as const satisfies Record<string, SectionSourceOption>;

const OPERATIONS_SECTION_SOURCE_OPTIONS: Record<OperationsDocumentType, SectionSourceOption[]> = {
  job_card: [
    OPERATION_SECTION_SOURCE_LIBRARY.overview,
    OPERATION_SECTION_SOURCE_LIBRARY.client_contact,
    OPERATION_SECTION_SOURCE_LIBRARY.personnel,
    OPERATION_SECTION_SOURCE_LIBRARY.attendance_timeline,
    OPERATION_SECTION_SOURCE_LIBRARY.subtasks,
    OPERATION_SECTION_SOURCE_LIBRARY.billing_summary,
    OPERATION_SECTION_SOURCE_LIBRARY.expenses,
    OPERATION_SECTION_SOURCE_LIBRARY.closing_details,
    OPERATION_SECTION_SOURCE_LIBRARY.external_comments,
    OPERATION_SECTION_SOURCE_LIBRARY.internal_comments,
    OPERATION_SECTION_SOURCE_LIBRARY.attachments,
    OPERATION_SECTION_SOURCE_LIBRARY.additional_info,
  ],
  attendance_card: [
    OPERATION_SECTION_SOURCE_LIBRARY.overview,
    OPERATION_SECTION_SOURCE_LIBRARY.client_contact,
    OPERATION_SECTION_SOURCE_LIBRARY.personnel,
    OPERATION_SECTION_SOURCE_LIBRARY.attendance_timeline,
    OPERATION_SECTION_SOURCE_LIBRARY.closing_details,
    OPERATION_SECTION_SOURCE_LIBRARY.external_comments,
    OPERATION_SECTION_SOURCE_LIBRARY.internal_comments,
    OPERATION_SECTION_SOURCE_LIBRARY.additional_info,
  ],
  task_card: [
    OPERATION_SECTION_SOURCE_LIBRARY.overview,
    OPERATION_SECTION_SOURCE_LIBRARY.client_contact,
    OPERATION_SECTION_SOURCE_LIBRARY.personnel,
    OPERATION_SECTION_SOURCE_LIBRARY.attendance_timeline,
    OPERATION_SECTION_SOURCE_LIBRARY.subtasks,
    OPERATION_SECTION_SOURCE_LIBRARY.closing_details,
    OPERATION_SECTION_SOURCE_LIBRARY.external_comments,
    OPERATION_SECTION_SOURCE_LIBRARY.internal_comments,
    OPERATION_SECTION_SOURCE_LIBRARY.additional_info,
  ],
  service_report: [
    OPERATION_SECTION_SOURCE_LIBRARY.overview,
    OPERATION_SECTION_SOURCE_LIBRARY.client_contact,
    OPERATION_SECTION_SOURCE_LIBRARY.personnel,
    OPERATION_SECTION_SOURCE_LIBRARY.attendance_timeline,
    OPERATION_SECTION_SOURCE_LIBRARY.expenses,
    OPERATION_SECTION_SOURCE_LIBRARY.closing_details,
    OPERATION_SECTION_SOURCE_LIBRARY.external_comments,
    OPERATION_SECTION_SOURCE_LIBRARY.internal_comments,
    OPERATION_SECTION_SOURCE_LIBRARY.attachments,
    OPERATION_SECTION_SOURCE_LIBRARY.additional_info,
  ],
  work_order: [
    OPERATION_SECTION_SOURCE_LIBRARY.overview,
    OPERATION_SECTION_SOURCE_LIBRARY.client_contact,
    OPERATION_SECTION_SOURCE_LIBRARY.personnel,
    OPERATION_SECTION_SOURCE_LIBRARY.attendance_timeline,
    OPERATION_SECTION_SOURCE_LIBRARY.expenses,
    OPERATION_SECTION_SOURCE_LIBRARY.closing_details,
    OPERATION_SECTION_SOURCE_LIBRARY.external_comments,
    OPERATION_SECTION_SOURCE_LIBRARY.internal_comments,
    OPERATION_SECTION_SOURCE_LIBRARY.attachments,
    OPERATION_SECTION_SOURCE_LIBRARY.additional_info,
  ],
  vehicle_inspection_report: [
    OPERATION_SECTION_SOURCE_LIBRARY.overview,
    OPERATION_SECTION_SOURCE_LIBRARY.vehicle_details,
    OPERATION_SECTION_SOURCE_LIBRARY.inspection_capture,
    OPERATION_SECTION_SOURCE_LIBRARY.issues_summary,
    OPERATION_SECTION_SOURCE_LIBRARY.notes,
    OPERATION_SECTION_SOURCE_LIBRARY.ai_review,
  ],
};

const SHARED_OVERVIEW_FIELD_OPTIONS: SectionFieldOption[] = [
  { key: 'title', label: 'Title' },
  { key: 'number', label: 'Reference Number' },
  { key: 'type', label: 'Type' },
  { key: 'appointments_covered', label: 'Appointments Covered' },
  { key: 'appointment_references', label: 'Appointment References' },
  { key: 'grouped_appointments', label: 'Linked Appointments' },
  { key: 'status', label: 'Status' },
  { key: 'priority', label: 'Priority' },
  { key: 'scheduled_date', label: 'Scheduled Date' },
  { key: 'start_time', label: 'Start Time' },
  { key: 'end_time', label: 'End Time' },
  { key: 'duration', label: 'Scheduled Duration' },
  { key: 'scheduled_window', label: 'Group Schedule Window' },
  { key: 'description', label: 'Description' },
  { key: 'completion_notes', label: 'Completion Notes' },
  { key: 'parent_job', label: 'Parent Job' },
];

const SHARED_CLIENT_CONTACT_FIELD_OPTIONS: SectionFieldOption[] = [
  { key: 'client_name', label: 'Client / Company' },
  { key: 'contact_person', label: 'Contact Person' },
  { key: 'client_phone', label: 'Phone' },
  { key: 'client_email', label: 'Email' },
  { key: 'location_name', label: 'Site / Location' },
  { key: 'location_address', label: 'Address' },
];

const SHARED_TIMELINE_FIELD_OPTIONS: SectionFieldOption[] = [
  { key: 'travel_start', label: 'Travel Started' },
  { key: 'travel_end', label: 'Travel Ended' },
  { key: 'travel_duration', label: 'Travel Duration' },
  { key: 'travel_distance', label: 'Travel Distance' },
  { key: 'work_started', label: 'Work Started' },
  { key: 'work_ended', label: 'Work Ended' },
  { key: 'on_site_duration', label: 'On-Site Duration' },
  { key: 'work_status', label: 'Work Status' },
  { key: 'started_at', label: 'Task Started' },
  { key: 'completed_at', label: 'Task Completed' },
  { key: 'total_work_duration', label: 'Total Duration' },
];
// Section config (not per-field): attendance_timeline.showExactTimes — use date+time for timeline instants instead of date-only.

const SHARED_ADDITIONAL_INFO_FIELD_OPTIONS: SectionFieldOption[] = [
  { key: 'notes', label: 'Notes' },
  { key: 'safety_reqs', label: 'Safety Requirements' },
  { key: 'equipment_req', label: 'Equipment Required' },
];

const VEHICLE_INSPECTION_OVERVIEW_FIELD_OPTIONS: SectionFieldOption[] = [
  { key: 'inspection_title', label: 'Inspection Title' },
  { key: 'inspection_reference', label: 'Inspection Reference' },
  { key: 'inspection_type', label: 'Inspection Type' },
  { key: 'inspection_status', label: 'Inspection Status' },
  { key: 'overall_condition', label: 'Overall Condition' },
  { key: 'inspection_date', label: 'Inspection Date' },
  { key: 'inspection_time', label: 'Inspection Time' },
  { key: 'follow_up_required', label: 'Follow-up Required' },
  { key: 'review_status', label: 'Review Status' },
  { key: 'issue_count', label: 'Issue Count' },
];

const VEHICLE_INSPECTION_DETAILS_FIELD_OPTIONS: SectionFieldOption[] = [
  { key: 'vehicle_registration', label: 'Registration' },
  { key: 'vehicle_name', label: 'Vehicle Name' },
  { key: 'vehicle_make', label: 'Make' },
  { key: 'vehicle_model', label: 'Model' },
  { key: 'vehicle_year', label: 'Year' },
  { key: 'vehicle_vin', label: 'VIN / Chassis' },
  { key: 'assigned_driver', label: 'Assigned Driver' },
  { key: 'vehicle_group', label: 'Vehicle Group' },
];

const VEHICLE_INSPECTION_CAPTURE_FIELD_OPTIONS: SectionFieldOption[] = [
  { key: 'inspector_name', label: 'Inspector' },
  { key: 'odometer_reading', label: 'Odometer Reading' },
  { key: 'fuel_level', label: 'Fuel Level' },
  { key: 'gps_coordinates', label: 'GPS Coordinates' },
  { key: 'gps_accuracy', label: 'GPS Accuracy' },
  { key: 'inspection_location', label: 'Inspection Location' },
  { key: 'photo_count', label: 'Photos Captured' },
];

const VEHICLE_INSPECTION_ISSUES_FIELD_OPTIONS: SectionFieldOption[] = [
  { key: 'issues_found', label: 'Issues Found' },
  { key: 'review_required', label: 'Review Required' },
  { key: 'review_status', label: 'Review Status' },
  { key: 'ai_damage_detected', label: 'AI Damage Detected' },
  { key: 'ai_new_damage_detected', label: 'New AI Damage Detected' },
  { key: 'ai_damage_summary', label: 'AI Damage Summary' },
];

const VEHICLE_INSPECTION_AI_REVIEW_FIELD_OPTIONS: SectionFieldOption[] = [
  { key: 'ai_overall_risk', label: 'AI Overall Risk' },
  { key: 'ai_overall_condition', label: 'AI Overall Condition' },
  { key: 'ai_condition_trend', label: 'Condition Trend' },
  { key: 'ai_executive_summary', label: 'Executive Summary' },
  { key: 'ai_priority_concerns', label: 'Priority Concerns' },
  { key: 'ai_recommended_actions', label: 'Recommended Actions' },
  { key: 'ai_accountability_notes', label: 'Accountability Notes' },
];

const VEHICLE_INSPECTION_NOTES_FIELD_OPTIONS: SectionFieldOption[] = [
  { key: 'notes', label: 'Inspector Notes' },
  { key: 'checklist_summary', label: 'Checklist Summary' },
  { key: 'capture_summary', label: 'Capture Summary' },
];

const CLOSING_DETAILS_FIELD_OPTIONS: SectionFieldOption[] = [
  { key: 'closing_details_*', label: 'Closing detail entries' },
];
const EXTERNAL_COMMENTS_FIELD_OPTIONS: SectionFieldOption[] = [
  { key: 'external_comment_*', label: 'External comment entries' },
];
const INTERNAL_COMMENTS_FIELD_OPTIONS: SectionFieldOption[] = [
  { key: 'internal_comment_*', label: 'Internal comment entries' },
];

const OPERATIONS_SECTION_FIELD_OPTIONS: Record<OperationsDocumentType, Partial<Record<string, SectionFieldOption[]>>> = {
  job_card: {
    overview: SHARED_OVERVIEW_FIELD_OPTIONS,
    client_contact: SHARED_CLIENT_CONTACT_FIELD_OPTIONS,
    personnel: [
      { key: 'assigned_to', label: 'Assigned To' },
      { key: 'team_members', label: 'Team Members' },
    ],
    attendance_timeline: SHARED_TIMELINE_FIELD_OPTIONS,
    closing_details: CLOSING_DETAILS_FIELD_OPTIONS,
    external_comments: EXTERNAL_COMMENTS_FIELD_OPTIONS,
    internal_comments: INTERNAL_COMMENTS_FIELD_OPTIONS,
    billing_summary: [
      { key: 'total_materials', label: 'Total Materials' },
      { key: 'total_labour', label: 'Total Labour' },
      { key: 'grand_total', label: 'Grand Total' },
    ],
    additional_info: SHARED_ADDITIONAL_INFO_FIELD_OPTIONS,
  },
  attendance_card: {
    overview: SHARED_OVERVIEW_FIELD_OPTIONS,
    client_contact: SHARED_CLIENT_CONTACT_FIELD_OPTIONS,
    personnel: [
      { key: 'assigned_to', label: 'Assigned To' },
      { key: 'team_members', label: 'Team Members' },
    ],
    attendance_timeline: SHARED_TIMELINE_FIELD_OPTIONS,
    closing_details: CLOSING_DETAILS_FIELD_OPTIONS,
    external_comments: EXTERNAL_COMMENTS_FIELD_OPTIONS,
    internal_comments: INTERNAL_COMMENTS_FIELD_OPTIONS,
    additional_info: SHARED_ADDITIONAL_INFO_FIELD_OPTIONS,
  },
  task_card: {
    overview: SHARED_OVERVIEW_FIELD_OPTIONS,
    client_contact: SHARED_CLIENT_CONTACT_FIELD_OPTIONS,
    personnel: [
      { key: 'assigned_to', label: 'Assigned To' },
      { key: 'team_members', label: 'Team Members' },
    ],
    attendance_timeline: SHARED_TIMELINE_FIELD_OPTIONS,
    subtasks: [
      { key: 'subtask_*', label: 'Subtask entries' },
    ],
    closing_details: CLOSING_DETAILS_FIELD_OPTIONS,
    external_comments: EXTERNAL_COMMENTS_FIELD_OPTIONS,
    internal_comments: INTERNAL_COMMENTS_FIELD_OPTIONS,
    additional_info: SHARED_ADDITIONAL_INFO_FIELD_OPTIONS,
  },
    service_report: {
    overview: SHARED_OVERVIEW_FIELD_OPTIONS,
    client_contact: SHARED_CLIENT_CONTACT_FIELD_OPTIONS,
    personnel: [
      { key: 'assigned_to', label: 'Assigned To' },
      { key: 'team_members', label: 'Team Members' },
    ],
    attendance_timeline: SHARED_TIMELINE_FIELD_OPTIONS,
    closing_details: CLOSING_DETAILS_FIELD_OPTIONS,
    external_comments: EXTERNAL_COMMENTS_FIELD_OPTIONS,
    internal_comments: INTERNAL_COMMENTS_FIELD_OPTIONS,
    additional_info: SHARED_ADDITIONAL_INFO_FIELD_OPTIONS,
  },
  work_order: {
    overview: SHARED_OVERVIEW_FIELD_OPTIONS,
    client_contact: SHARED_CLIENT_CONTACT_FIELD_OPTIONS,
    personnel: [
      { key: 'assigned_to', label: 'Assigned To' },
      { key: 'team_members', label: 'Team Members' },
    ],
    attendance_timeline: SHARED_TIMELINE_FIELD_OPTIONS,
    closing_details: CLOSING_DETAILS_FIELD_OPTIONS,
    external_comments: EXTERNAL_COMMENTS_FIELD_OPTIONS,
    internal_comments: INTERNAL_COMMENTS_FIELD_OPTIONS,
    additional_info: SHARED_ADDITIONAL_INFO_FIELD_OPTIONS,
  },
  vehicle_inspection_report: {
    overview: VEHICLE_INSPECTION_OVERVIEW_FIELD_OPTIONS,
    vehicle_details: VEHICLE_INSPECTION_DETAILS_FIELD_OPTIONS,
    inspection_capture: VEHICLE_INSPECTION_CAPTURE_FIELD_OPTIONS,
    issues_summary: VEHICLE_INSPECTION_ISSUES_FIELD_OPTIONS,
    ai_review: VEHICLE_INSPECTION_AI_REVIEW_FIELD_OPTIONS,
    notes: VEHICLE_INSPECTION_NOTES_FIELD_OPTIONS,
  },
};

function cloneSectionSource(source: SectionSourceOption): SectionSourceOption {
  return { ...source };
}

export function getOperationsSectionSourceOptions(docType: DocumentType): SectionSourceOption[] {
  if (!isOperationsDocumentType(docType)) return [];
  return (OPERATIONS_SECTION_SOURCE_OPTIONS[docType] || []).map(cloneSectionSource);
}

export function getOperationsSectionFieldOptions(docType: DocumentType, sectionKey?: string): SectionFieldOption[] {
  if (!isOperationsDocumentType(docType) || !sectionKey) return [];
  return (OPERATIONS_SECTION_FIELD_OPTIONS[docType]?.[sectionKey] || []).map((option) => ({ ...option }));
}

function operationSectionFields(
  docType: OperationsDocumentType,
  sourceKey: string,
  enabled?: boolean,
): UnifiedTemplateSection {
  const source = getOperationsSectionSourceOptions(docType).find((option) => option.key === sourceKey) || {
    key: sourceKey,
    label: sourceKey.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    enabledByDefault: true,
  };

  return {
    ...section('section_fields', source.label, { sectionKey: source.key }),
    enabled: enabled ?? source.enabledByDefault !== false,
  };
}

function sectionIdentityKey(section: UnifiedTemplateSection): string {
  if (section.type === 'section_fields') {
    return `${section.type}:${section.config?.sectionKey || ''}`;
  }
  if (section.type === 'fields_table') {
    const target = section.config?.sectionKey || '';
    const excluded = Array.isArray(section.config?.excludeSectionKeys)
      ? section.config.excludeSectionKeys.join('|')
      : '';
    return `${section.type}:${target}:${excluded}`;
  }
  return section.type;
}

function mergeTemplateSections(
  defaults: UnifiedTemplateSection[],
  existing: UnifiedTemplateSection[],
): UnifiedTemplateSection[] {
  const existingByIdentity = new Map(existing.map((section) => [sectionIdentityKey(section), section]));

  return defaults.map((defaultSection) => {
    const match = existingByIdentity.get(sectionIdentityKey(defaultSection));

    // Section not in existing template — add it but disabled so it doesn't
    // surprise the client with sections their template was designed without.
    if (!match) return { ...defaultSection, enabled: false };

    return {
      ...defaultSection,
      label: match.label || defaultSection.label,
      enabled: match.enabled ?? defaultSection.enabled,
      config: {
        ...defaultSection.config,
        ...(match.config || {}),
      },
    };
  });
}

export function normalizeTemplateSections(
  docType: DocumentType,
  sections: UnifiedTemplateSection[] | null | undefined,
): UnifiedTemplateSection[] {
  if (!Array.isArray(sections)) {
    return getDefaultSections(docType);
  }

  if (!isOperationsDocumentType(docType)) {
    return sections;
  }

  const hasGranularOperationSections = sections.some(
    (section) => section.type === 'section_fields' && typeof section.config?.sectionKey === 'string' && section.config.sectionKey.length > 0,
  );

  if (hasGranularOperationSections) {
    return sections;
  }

  return mergeTemplateSections(getDefaultSections(docType), sections);
}

// ============================================================================
// REUSABLE SECTION BUILDERS
// ============================================================================

/** Standard header section */
const header = (docLabel = 'Document') =>
  section('header', `${docLabel} Header`, {
    showLogo: true,
    showCompanyName: true,
    showFormTitle: true,
    /** When false, job/appointment status is omitted from the top header strip */
    showDocumentStatus: true,
  });

/** Company + contact (From/To) */
const companyInfo = () =>
  section('company_info', 'Company & Customer Details', {
    showCompanyLogo: true,
    showContactDetails: true,
    layout: 'two-column',
  });

/** Document details (number, dates, ref) */
const documentDetails = () =>
  section('document_details', 'Document Details', {
    fields: ['document_number', 'date', 'due_date', 'reference', 'po_number'],
  });

/** Line items */
const lineItems = (columns?: TemplateTableColumn[]) =>
  section('line_items_table', 'Line Items', {
    columns: columns || DEFAULT_LINE_ITEM_COLUMNS,
    showRowNumbers: false,
    alternateRowShading: true,
    zebraColor: '#f9fafb',
  });

/** Totals */
const totals = () =>
  section('totals_block', 'Totals', {
    showSubtotal: true,
    showDiscount: true,
    showTax: true,
    showGrandTotal: true,
    showAmountDue: true,
  });

/** Bank details */
const bankDetails = () =>
  section('bank_details', 'Banking Details', {
    showBankName: true,
    showAccountNumber: true,
    showBranchCode: true,
    showAccountType: true,
    showSwift: false,
    showIban: false,
    showReference: false,
  });

/** Terms & notes */
const termsNotes = () =>
  section('terms_notes', 'Terms & Notes', {
    showTerms: true,
    showNotes: true,
    showPaymentTerms: true,
  });

/** Payment info */
const paymentInfo = () =>
  section('payment_info', 'Payment Information', {
    showAmountPaid: true,
    showBalance: true,
    showPaymentHistory: false,
  });

/** Footer */
const footer = () =>
  section('footer', 'Page Footer', {
    text: '',
    align: 'left',
  });

/** Signature block */
const signatureBlock = (label = 'Signatures') =>
  section('signature_block', label, {
    showSignatureImage: true,
    showSignerName: true,
    showTimestamp: false,
    showDate: true,
    showSourceAppointment: true,
    columns: 2,
  });

/** Custom text */
const customText = (label = 'Custom Text', text = '') =>
  section('custom_text', label, { text });

/** Section fields (grouped key-value fields by sectionKey) */
const sectionFields = (sectionKey: string, label?: string) =>
  section('section_fields', label || sectionKey.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()), { sectionKey });

/** Meta info (form/ops cards) */
const metaInfo = (fields: string[] = []) =>
  section('meta_info', 'Summary Information', { fields, layout: 'cards' });

/** Form fields table */
const fieldsTable = () =>
  section('fields_table', 'Form Fields', {
    showAllFields: true,
    excludeEmpty: false,
    groupBySection: false,
  });

/** Certificate box */
const certificateBox = () =>
  section('certificate_box', 'Certificate', {
    showCertNumber: true,
    showIssuedDate: true,
    showExpiryDate: true,
    showQRCode: false,
    showBadge: true,
  });

/** KPI cards (reports) */
const kpiCards = (items: Array<{ label: string; valueTag: string }> = []) =>
  section('kpi_cards', 'KPI Summary', { items, columns: 4 });

/** Data table (reports) */
const dataTable = (label: string, columns: TemplateTableColumn[] = [], tableKey?: string) =>
  section('data_table', label, {
    ...(tableKey ? { tableKey } : {}),
    columns,
    showRowNumbers: false,
    alternateRowShading: true,
    zebraColor: '#f9fafb',
    showTotalRow: false,
  });

/** Summary/total row */
const summaryRow = (label = 'Total') =>
  section('summary_row', label, { highlightColor: '#f3f4f6', bold: true });

/** Two-column layout (reports) */
const twoColumn = (leftLabel = 'Left', rightLabel = 'Right') =>
  section('two_column', 'Two-Column Layout', {
    leftSection: { label: leftLabel, columns: [] },
    rightSection: { label: rightLabel, columns: [] },
  });

/** Contact / site info */
const contactInfo = () =>
  section('contact_info', 'Contact / Site Info', {
    showAddress: true,
    showPhone: true,
    showEmail: true,
    showAssignedTo: true,
    showGPS: false,
  });

/** Checklist */
const checklist = () =>
  section('checklist', 'Checklist', {
    showStatus: true,
    statusOptions: ['pass', 'fail', 'n/a'],
  });

/** Time log */
const timeLog = () =>
  section('time_log', 'Time Log', {
    showStartEnd: true,
    showDuration: true,
    showEmployee: true,
    showDescription: true,
    showTotalHours: true,
    showTravelEntries: true,
  });

/** Materials / BOM */
const materialsBOM = () =>
  section('materials_bom', 'Materials Used', {
    showQuantity: true,
    showCost: false,
    showTotal: false,
  });

/** Photo grid */
const photoGrid = () =>
  section('photo_grid', 'Photo Evidence', {
    columns: 3,
    showCaptions: true,
    showPhotoCaption: true,
    showCapturedByName: true,
    showUploaderInitials: false,
    showSourceAppointment: true,
    showPhotoTimestamp: true,
    photoTimestampPrecision: 'date',
    showGpsCoordinates: true,
    maxPhotos: 12,
  });

/** GPS location */
const gpsLocation = () =>
  section('gps_location', 'GPS Location', {
    showCoordinates: true,
    showAddress: true,
    showMap: false,
  });

function buildOperationsSections(docType: OperationsDocumentType): UnifiedTemplateSection[] {
  switch (docType) {
    case 'job_card':
      return [
        header('Job Card'),
        contactInfo(),
        operationSectionFields('job_card', 'overview', true),
        operationSectionFields('job_card', 'personnel', true),
        operationSectionFields('job_card', 'attendance_timeline', true),
        checklist(),
        materialsBOM(),
        timeLog(),
        operationSectionFields('job_card', 'additional_info', false),
        operationSectionFields('job_card', 'expenses', false),
        operationSectionFields('job_card', 'closing_details', false),
        operationSectionFields('job_card', 'external_comments', false),
        operationSectionFields('job_card', 'internal_comments', false),
        operationSectionFields('job_card', 'attachments', false),
        signatureBlock(),
        bankDetails(),
        footer(),
      ];

    case 'attendance_card':
      return [
        header('Attendance Card'),
        contactInfo(),
        operationSectionFields('attendance_card', 'overview', true),
        operationSectionFields('attendance_card', 'personnel', true),
        operationSectionFields('attendance_card', 'attendance_timeline', true),
        timeLog(),
        gpsLocation(),
        photoGrid(),
        operationSectionFields('attendance_card', 'closing_details', false),
        signatureBlock(),
        operationSectionFields('attendance_card', 'external_comments', false),
        operationSectionFields('attendance_card', 'internal_comments', false),
        bankDetails(),
        footer(),
      ];

    case 'task_card':
      return [
        header('Task Card'),
        contactInfo(),
        operationSectionFields('task_card', 'overview', true),
        operationSectionFields('task_card', 'personnel', true),
        operationSectionFields('task_card', 'attendance_timeline', true),
        operationSectionFields('task_card', 'subtasks', true),
        checklist(),
        timeLog(),
        operationSectionFields('task_card', 'additional_info', true),
        operationSectionFields('task_card', 'closing_details', false),
        operationSectionFields('task_card', 'external_comments', false),
        operationSectionFields('task_card', 'internal_comments', false),
        photoGrid(),
        signatureBlock(),
        footer(),
      ];

    case 'service_report':
      return [
        header('Service Report'),
        contactInfo(),
        operationSectionFields('service_report', 'overview', true),
        operationSectionFields('service_report', 'personnel', true),
        operationSectionFields('service_report', 'attendance_timeline', true),
        checklist(),
        materialsBOM(),
        timeLog(),
        operationSectionFields('service_report', 'additional_info', true),
        operationSectionFields('service_report', 'expenses', false),
        operationSectionFields('service_report', 'closing_details', false),
        operationSectionFields('service_report', 'external_comments', false),
        operationSectionFields('service_report', 'internal_comments', false),
        operationSectionFields('service_report', 'attachments', false),
        photoGrid(),
        signatureBlock(),
        bankDetails(),
        footer(),
      ];

    case 'work_order':
      return [
        header('Work Order'),
        contactInfo(),
        operationSectionFields('work_order', 'overview', true),
        operationSectionFields('work_order', 'personnel', true),
        operationSectionFields('work_order', 'attendance_timeline', true),
        operationSectionFields('work_order', 'additional_info', true),
        checklist(),
        materialsBOM(),
        timeLog(),
        operationSectionFields('work_order', 'expenses', false),
        operationSectionFields('work_order', 'closing_details', false),
        operationSectionFields('work_order', 'external_comments', false),
        operationSectionFields('work_order', 'internal_comments', false),
        operationSectionFields('work_order', 'attachments', false),
        signatureBlock('Assigned To'),
        bankDetails(),
        footer(),
      ];

    case 'vehicle_inspection_report':
      return [
        header('Vehicle Inspection Report'),
        operationSectionFields('vehicle_inspection_report', 'overview', true),
        operationSectionFields('vehicle_inspection_report', 'vehicle_details', true),
        operationSectionFields('vehicle_inspection_report', 'inspection_capture', true),
        checklist(),
        photoGrid(),
        gpsLocation(),
        operationSectionFields('vehicle_inspection_report', 'issues_summary', true),
        operationSectionFields('vehicle_inspection_report', 'notes', true),
        operationSectionFields('vehicle_inspection_report', 'ai_review', true),
        footer(),
      ];
  }
}

export function getDefaultComprehensiveOperationsSections(docType: OperationsDocumentType): UnifiedTemplateSection[] {
  const excludedSectionKeys = [
    'overview',
    'personnel',
    'attendance_timeline',
    'subtasks',
    'billing_summary',
    'expenses',
    'closing_details',
    'external_comments',
    'internal_comments',
    'attachments',
    'additional_info',
    'vehicle_details',
    'inspection_capture',
    'issues_summary',
    'notes',
    'ai_review',
  ];

  return [
    header(DOCUMENT_TYPE_LABELS[docType] || 'Operations Report'),
    contactInfo(),
    operationSectionFields(docType, 'overview', true),
    operationSectionFields(docType, 'personnel', true),
    operationSectionFields(docType, 'attendance_timeline', true),
    operationSectionFields(docType, 'subtasks', true),
    checklist(),
    timeLog(),
    materialsBOM(),
    operationSectionFields(docType, 'billing_summary', true),
    operationSectionFields(docType, 'expenses', true),
    operationSectionFields(docType, 'closing_details', true),
    operationSectionFields(docType, 'external_comments', true),
    operationSectionFields(docType, 'internal_comments', true),
    operationSectionFields(docType, 'attachments', true),
    operationSectionFields(docType, 'additional_info', true),
    {
      ...section('fields_table', 'Forms & Additional Sections', {
        excludeSectionKeys: excludedSectionKeys,
      }),
      enabled: true,
    },
    gpsLocation(),
    photoGrid(),
    signatureBlock(),
    termsNotes(),
    certificateBox(),
    footer(),
  ];
}

// ============================================================================
// DEFAULT SECTIONS PER DOCUMENT TYPE
// ============================================================================

type SectionFactory = () => UnifiedTemplateSection[];

const FINANCE_DOCUMENT_DEFAULTS: Record<string, SectionFactory> = {
  price_list: () => [
    header('Price List'),
    companyInfo(),
    documentDetails(),
    metaInfo(['status', 'currency', 'customer', 'issued_date']),
    lineItems([
      { key: 'description', label: 'Item', width: '36%', show: true, align: 'left', format: 'text' },
      { key: 'itemCode', label: 'Code', width: '12%', show: true, align: 'left', format: 'text' },
      { key: 'quantity', label: 'Qty', width: '8%', show: true, align: 'center', format: 'number' },
      { key: 'unit', label: 'Unit', width: '10%', show: true, align: 'center', format: 'text' },
      { key: 'unitPrice', label: 'Resolved Price', width: '16%', show: true, align: 'right', format: 'currency' },
      { key: 'tax', label: 'Tax', width: '8%', show: true, align: 'right', format: 'percent' },
      { key: 'total', label: 'Line Total', width: '18%', show: true, align: 'right', format: 'currency' },
    ]),
    totals(),
    termsNotes(),
    footer(),
  ],
  invoice: () => [
    header('Invoice'), companyInfo(), documentDetails(), lineItems(), totals(),
    paymentInfo(), bankDetails(), termsNotes(), footer(),
  ],
  invoice_batch: () => [
    header('Invoice Batch'), companyInfo(), documentDetails(),
    metaInfo(['status', 'total_documents', 'approved_count', 'pending_count', 'rejected_count']),
    dataTable('Invoices in Batch', [
      { key: 'invoice', label: 'Invoice', width: '15%', show: true, align: 'left', format: 'text' },
      { key: 'customer', label: 'Customer', width: '30%', show: true, align: 'left', format: 'text' },
      { key: 'due_date', label: 'Due Date', width: '13%', show: true, align: 'left', format: 'date' },
      { key: 'doc_status', label: 'Doc Status', width: '12%', show: true, align: 'left', format: 'text' },
      { key: 'item_status', label: 'Batch Status', width: '12%', show: true, align: 'left', format: 'text' },
      { key: 'amount', label: 'Amount', width: '18%', show: true, align: 'right', format: 'currency' },
    ], 'batch_invoices'),
    totals(), termsNotes(), footer(),
  ],
  quote: () => [
    header('Quote'), companyInfo(), documentDetails(), lineItems(), totals(),
    termsNotes(), signatureBlock('Acceptance'), footer(),
  ],
  credit_note: () => [
    header('Credit Note'), companyInfo(), documentDetails(), lineItems(), totals(),
    customText('Reason', ''), footer(),
  ],
  debit_note: () => [
    header('Debit Note'), companyInfo(), documentDetails(), lineItems(), totals(),
    customText('Reason', ''), footer(),
  ],
  supplier_invoice: () => [
    header('Supplier Invoice'), companyInfo(), documentDetails(), lineItems(), totals(),
    termsNotes(), footer(),
  ],
  receipt: () => [
    header('Receipt'), companyInfo(), documentDetails(), lineItems(), totals(),
    paymentInfo(), footer(),
  ],
  statement: () => [
    header('Statement'), companyInfo(), documentDetails(), lineItems(), totals(),
    paymentInfo(), footer(),
  ],
  purchase_order: () => [
    header('Purchase Order'), companyInfo(), documentDetails(), lineItems(), totals(),
    termsNotes(), signatureBlock('Authorisation'), footer(),
  ],
  purchase_requisition: () => [
    header('Purchase Requisition'), companyInfo(), documentDetails(), lineItems(), totals(),
    termsNotes(), signatureBlock('Approval'), footer(),
  ],
  delivery_note: () => [
    header('Delivery Note'), companyInfo(), documentDetails(),
    lineItems(DELIVERY_NOTE_COLUMNS),
    signatureBlock('Received By'), footer(),
  ],
  waybill: () => [
    header('Waybill'), companyInfo(), documentDetails(),
    lineItems(DELIVERY_NOTE_COLUMNS),
    signatureBlock('Received By'), footer(),
  ],
  picking_slip: () => [
    header('Picking Slip'), companyInfo(), documentDetails(),
    lineItems(WAREHOUSE_MOVEMENT_COLUMNS),
    termsNotes(), signatureBlock('Picked By'), footer(),
  ],
  packing_slip: () => [
    header('Packing Slip'), companyInfo(), documentDetails(),
    lineItems(WAREHOUSE_MOVEMENT_COLUMNS),
    termsNotes(), signatureBlock('Packed By'), footer(),
  ],
  stock_adjustment: () => [
    header('Stock Adjustment'), companyInfo(), documentDetails(),
    lineItems(WAREHOUSE_MOVEMENT_COLUMNS),
    termsNotes(), signatureBlock('Approved By'), footer(),
  ],
  transfer_order: () => [
    header('Transfer Order'), companyInfo(), documentDetails(),
    lineItems(WAREHOUSE_MOVEMENT_COLUMNS),
    termsNotes(), signatureBlock('Received By'), footer(),
  ],
  grn: () => [
    header('Goods Received Note'), companyInfo(), documentDetails(),
    lineItems(DELIVERY_NOTE_COLUMNS),
    signatureBlock('Received By'), footer(),
  ],
  sales_order: () => [
    header('Sales Order'), companyInfo(), documentDetails(), lineItems(), totals(),
    termsNotes(), footer(),
  ],
  proforma: () => [
    header('Proforma Invoice'), companyInfo(), documentDetails(), lineItems(), totals(),
    bankDetails(), termsNotes(), footer(),
  ],
  remittance: () => [
    header('Remittance Advice'), companyInfo(), documentDetails(), lineItems(), totals(),
    bankDetails(), footer(),
  ],
  supplier_quote: () => [
    header('Supplier Quote'), companyInfo(), documentDetails(), lineItems(), totals(),
    termsNotes(), signatureBlock('Acceptance'), footer(),
  ],
  supplier_credit: () => [
    header('Supplier Credit'), companyInfo(), documentDetails(), lineItems(), totals(),
    customText('Reason', ''), footer(),
  ],
  rfq: () => [
    header('Request for Quote'), companyInfo(), documentDetails(), lineItems(), totals(),
    termsNotes(), footer(),
  ],
  purchase_return: () => [
    header('Purchase Return'), companyInfo(), documentDetails(), lineItems(), totals(),
    customText('Return Reason', ''), signatureBlock('Authorisation'), footer(),
  ],
  rma: () => [
    header('Return Merchandise Authorisation'), companyInfo(), documentDetails(),
    lineItems(DELIVERY_NOTE_COLUMNS),
    customText('Return Notes', ''), signatureBlock('Authorisation'), footer(),
  ],
};

const FORM_DEFAULTS: Record<string, SectionFactory> = {
  form_submission: () => [
    header('Form Submission'), metaInfo(['submitted_by', 'submission_date', 'status']),
    fieldsTable(), photoGrid(), gpsLocation(), signatureBlock(), footer(),
  ],
  form_certificate: () => [
    header('Certificate'), metaInfo(['recipient', 'date_issued']),
    certificateBox(), fieldsTable(), signatureBlock(), footer(),
  ],
  quiz_certificate: () => [
    header('Quiz Certificate'), metaInfo(['candidate', 'score', 'date']),
    certificateBox(), footer(),
  ],
};

const OPS_DEFAULTS: Record<string, SectionFactory> = {
  job_card: () => buildOperationsSections('job_card'),
  attendance_card: () => buildOperationsSections('attendance_card'),
  task_card: () => buildOperationsSections('task_card'),
  service_report: () => buildOperationsSections('service_report'),
  work_order: () => buildOperationsSections('work_order'),
  vehicle_inspection_report: () => buildOperationsSections('vehicle_inspection_report'),
  // Software development roadmap — epics overview then a catch-all detail
  // table section that renders the adapter's dynamic per-epic children,
  // milestones and unlinked-item tables in input order.
  software_roadmap: () => [
    header('Software Development Roadmap'),
    kpiCards(),
    sectionFields('roadmap_scope', 'Roadmap Scope'),
    dataTable('Epics', [
      { key: 'item_number', label: 'Epic #', show: true, align: 'left', format: 'text' },
      { key: 'title', label: 'Epic', show: true, align: 'left', format: 'text' },
      { key: 'quarter', label: 'Quarter', show: true, align: 'center', format: 'text' },
      { key: 'horizon', label: 'Horizon', show: true, align: 'center', format: 'text' },
      { key: 'status', label: 'Status', show: true, align: 'left', format: 'text' },
      { key: 'target', label: 'Target', show: true, align: 'left', format: 'text' },
      { key: 'items', label: 'Items', show: true, align: 'right', format: 'number' },
      { key: 'done', label: 'Done', show: true, align: 'right', format: 'number' },
      { key: 'progress', label: 'Progress', show: true, align: 'right', format: 'text' },
    ], 'epic_overview'),
    dataTable('Roadmap Detail'),
    footer(),
  ],
  drawing_transmittal: () => [
    header('Drawing transmittal register'),
    companyInfo(),
    section('meta_info', 'Register summary', {}),
    section('data_table', 'Drawings on record', {
      tableKey: 'drawings_register',
      columns: [
        { key: 'drawing_number', label: 'Drawing No.', show: true, align: 'left', format: 'text' },
        { key: 'name', label: 'Title', show: true, align: 'left', format: 'text' },
        { key: 'revision', label: 'Rev', show: true, align: 'center', format: 'text' },
        { key: 'version', label: 'Ver', show: true, align: 'center', format: 'text' },
        { key: 'status', label: 'Status', show: true, align: 'left', format: 'text' },
        { key: 'published', label: 'Issued', show: true, align: 'center', format: 'text' },
        { key: 'issued', label: 'Issued date', show: true, align: 'left', format: 'text' },
      ],
      showRowNumbers: false,
      alternateRowShading: true,
      zebraColor: '#f9fafb',
      showTotalRow: false,
    }),
    section('data_table', 'Transmittal history', {
      tableKey: 'transmittal_history',
      columns: [
        { key: 'number', label: 'Transmittal', show: true, align: 'left', format: 'text' },
        { key: 'subject', label: 'Subject', show: true, align: 'left', format: 'text' },
        { key: 'purpose', label: 'Purpose', show: true, align: 'left', format: 'text' },
        { key: 'status', label: 'Status', show: true, align: 'left', format: 'text' },
        { key: 'sent', label: 'Sent', show: true, align: 'left', format: 'text' },
        { key: 'recipients', label: 'Rcpts', show: true, align: 'center', format: 'number' },
        { key: 'drawings', label: 'Dwgs', show: true, align: 'center', format: 'number' },
      ],
      showRowNumbers: false,
      alternateRowShading: true,
      zebraColor: '#f9fafb',
      showTotalRow: false,
    }),
    termsNotes(),
    footer(),
  ],
};

// Digital Voting — minutes / results / attendance register. All sections are
// keyed so the voting adapter can emit a single rich input per session and the
// template decides which render.
const VOTING_DEFAULTS: Record<string, SectionFactory> = {
  voting_minutes: () => [
    header('Meeting Minutes'),
    metaInfo(),
    sectionFields('session_details', 'Meeting Details'),
    sectionFields('voting_rules', 'Voting Rules'),
    sectionFields('quorum', 'Quorum & Participation'),
    checklist(),
    dataTable('Attendance Register', [], 'attendance'),
    dataTable('Voting Results', [], 'results'),
    timeLog(),
    signatureBlock('Certification'),
    termsNotes(),
    footer(),
  ],
  voting_results: () => [
    header('Voting Results'),
    metaInfo(),
    sectionFields('session_details', 'Session Details'),
    sectionFields('voting_rules', 'Voting Rules'),
    checklist(),
    dataTable('Voting Results', [], 'results'),
    signatureBlock('Certification'),
    termsNotes(),
    footer(),
  ],
  voting_attendance_register: () => [
    header('Attendance Register'),
    metaInfo(),
    sectionFields('session_details', 'Session Details'),
    sectionFields('quorum', 'Quorum & Participation'),
    dataTable('Attendance Register', [], 'attendance'),
    signatureBlock('Certification'),
    footer(),
  ],
};

const FINANCE_REPORT_DEFAULTS: Record<string, SectionFactory> = {
  profit_loss: () => [
    header('Profit & Loss'),
    kpiCards([
      { label: 'Revenue', valueTag: '{{ total_revenue }}' },
      { label: 'Expenses', valueTag: '{{ total_expenses }}' },
      { label: 'Net Profit', valueTag: '{{ net_profit }}' },
      { label: 'Margin', valueTag: '{{ profit_margin }}' },
    ]),
    dataTable('Revenue', [
      { key: 'account', label: 'Account', show: true, align: 'left', format: 'text' },
      { key: 'amount', label: 'Amount', show: true, align: 'right', format: 'currency' },
    ]),
    summaryRow('Total Revenue'),
    dataTable('Expenses', [
      { key: 'account', label: 'Account', show: true, align: 'left', format: 'text' },
      { key: 'amount', label: 'Amount', show: true, align: 'right', format: 'currency' },
    ]),
    summaryRow('Total Expenses'),
    summaryRow('Net Profit'),
    footer(),
  ],
  balance_sheet: () => [
    header('Balance Sheet'),
    twoColumn('Assets', 'Liabilities & Equity'),
    summaryRow('Total'),
    footer(),
  ],
  trial_balance: () => [
    header('Trial Balance'),
    dataTable('Trial Balance', [
      { key: 'account_code', label: 'Code', show: true, align: 'left', format: 'text' },
      { key: 'account_name', label: 'Account', show: true, align: 'left', format: 'text' },
      { key: 'debit', label: 'Debit', show: true, align: 'right', format: 'currency' },
      { key: 'credit', label: 'Credit', show: true, align: 'right', format: 'currency' },
    ]),
    summaryRow('Total'),
    footer(),
  ],
  cash_flow: () => [
    header('Cash Flow Statement'),
    kpiCards([
      { label: 'Opening', valueTag: '{{ opening_balance }}' },
      { label: 'Inflow', valueTag: '{{ total_inflow }}' },
      { label: 'Outflow', valueTag: '{{ total_outflow }}' },
      { label: 'Closing', valueTag: '{{ closing_balance }}' },
    ]),
    dataTable('Cash Flow', [
      { key: 'category', label: 'Category', show: true, align: 'left', format: 'text' },
      { key: 'amount', label: 'Amount', show: true, align: 'right', format: 'currency' },
    ]),
    footer(),
  ],
  aged_receivables: () => [
    header('Aged Receivables'),
    kpiCards([
      { label: 'Total Owed', valueTag: '{{ total_owed }}' },
      { label: 'Current', valueTag: '{{ current }}' },
      { label: '30 Days', valueTag: '{{ days_30 }}' },
      { label: '60+ Days', valueTag: '{{ days_60_plus }}' },
    ]),
    dataTable('Aged Receivables', [
      { key: 'customer', label: 'Customer', show: true, align: 'left', format: 'text' },
      { key: 'current', label: 'Current', show: true, align: 'right', format: 'currency' },
      { key: 'days_30', label: '30 Days', show: true, align: 'right', format: 'currency' },
      { key: 'days_60', label: '60 Days', show: true, align: 'right', format: 'currency' },
      { key: 'days_90', label: '90 Days', show: true, align: 'right', format: 'currency' },
      { key: 'days_120', label: '120+ Days', show: true, align: 'right', format: 'currency' },
      { key: 'total', label: 'Total', show: true, align: 'right', format: 'currency' },
    ]),
    summaryRow('Grand Total'),
    footer(),
  ],
  aged_payables: () => [
    header('Aged Payables'),
    kpiCards([
      { label: 'Total Owed', valueTag: '{{ total_owed }}' },
      { label: 'Current', valueTag: '{{ current }}' },
      { label: '30 Days', valueTag: '{{ days_30 }}' },
      { label: '60+ Days', valueTag: '{{ days_60_plus }}' },
    ]),
    dataTable('Aged Payables', [
      { key: 'supplier', label: 'Supplier', show: true, align: 'left', format: 'text' },
      { key: 'current', label: 'Current', show: true, align: 'right', format: 'currency' },
      { key: 'days_30', label: '30 Days', show: true, align: 'right', format: 'currency' },
      { key: 'days_60', label: '60 Days', show: true, align: 'right', format: 'currency' },
      { key: 'days_90', label: '90 Days', show: true, align: 'right', format: 'currency' },
      { key: 'days_120', label: '120+ Days', show: true, align: 'right', format: 'currency' },
      { key: 'total', label: 'Total', show: true, align: 'right', format: 'currency' },
    ]),
    summaryRow('Grand Total'),
    footer(),
  ],
  vat_report: () => [
    header('VAT Report'),
    kpiCards([
      { label: 'Output VAT', valueTag: '{{ output_vat }}' },
      { label: 'Input VAT', valueTag: '{{ input_vat }}' },
      { label: 'Net VAT', valueTag: '{{ net_vat }}' },
    ]),
    dataTable('VAT Breakdown', [
      { key: 'description', label: 'Description', show: true, align: 'left', format: 'text' },
      { key: 'taxable_amount', label: 'Taxable Amount', show: true, align: 'right', format: 'currency' },
      { key: 'vat_amount', label: 'VAT Amount', show: true, align: 'right', format: 'currency' },
    ]),
    summaryRow('Totals'),
    footer(),
  ],
  tax_summary: () => [
    header('Tax Summary'),
    dataTable('Tax Summary', [
      { key: 'tax_rate', label: 'Tax Rate', show: true, align: 'left', format: 'text' },
      { key: 'taxable_amount', label: 'Taxable Amount', show: true, align: 'right', format: 'currency' },
      { key: 'tax_amount', label: 'Tax Amount', show: true, align: 'right', format: 'currency' },
    ]),
    summaryRow('Total'),
    footer(),
  ],
  customer_statement: () => [
    header('Customer Statement'), companyInfo(),
    dataTable('Transactions', [
      { key: 'date', label: 'Date', show: true, align: 'left', format: 'date' },
      { key: 'reference', label: 'Reference', show: true, align: 'left', format: 'text' },
      { key: 'description', label: 'Description', show: true, align: 'left', format: 'text' },
      { key: 'debit', label: 'Debit', show: true, align: 'right', format: 'currency' },
      { key: 'credit', label: 'Credit', show: true, align: 'right', format: 'currency' },
      { key: 'balance', label: 'Balance', show: true, align: 'right', format: 'currency' },
    ]),
    summaryRow('Balance Due'),
    footer(),
  ],
  debtor_reconciliation: () => [
    header('Debtor Reconciliation'),
    dataTable('Reconciliation', [
      { key: 'customer', label: 'Customer', show: true, align: 'left', format: 'text' },
      { key: 'invoiced', label: 'Invoiced', show: true, align: 'right', format: 'currency' },
      { key: 'paid', label: 'Paid', show: true, align: 'right', format: 'currency' },
      { key: 'balance', label: 'Balance', show: true, align: 'right', format: 'currency' },
    ]),
    summaryRow('Total'),
    footer(),
  ],
  creditor_reconciliation: () => [
    header('Creditor Reconciliation'),
    dataTable('Reconciliation', [
      { key: 'supplier', label: 'Supplier', show: true, align: 'left', format: 'text' },
      { key: 'billed', label: 'Billed', show: true, align: 'right', format: 'currency' },
      { key: 'paid', label: 'Paid', show: true, align: 'right', format: 'currency' },
      { key: 'balance', label: 'Balance', show: true, align: 'right', format: 'currency' },
    ]),
    summaryRow('Total'),
    footer(),
  ],
  general_ledger: () => [
    header('General Ledger'),
    dataTable('General Ledger', [
      { key: 'date', label: 'Date', show: true, align: 'left', format: 'date' },
      { key: 'account', label: 'Account', show: true, align: 'left', format: 'text' },
      { key: 'description', label: 'Description', show: true, align: 'left', format: 'text' },
      { key: 'debit', label: 'Debit', show: true, align: 'right', format: 'currency' },
      { key: 'credit', label: 'Credit', show: true, align: 'right', format: 'currency' },
      { key: 'balance', label: 'Balance', show: true, align: 'right', format: 'currency' },
    ]),
    footer(),
  ],
  account_transactions: () => [
    header('Account Transactions'),
    dataTable('Transactions', [
      { key: 'date', label: 'Date', show: true, align: 'left', format: 'date' },
      { key: 'reference', label: 'Reference', show: true, align: 'left', format: 'text' },
      { key: 'description', label: 'Description', show: true, align: 'left', format: 'text' },
      { key: 'debit', label: 'Debit', show: true, align: 'right', format: 'currency' },
      { key: 'credit', label: 'Credit', show: true, align: 'right', format: 'currency' },
      { key: 'balance', label: 'Running Bal', show: true, align: 'right', format: 'currency' },
    ]),
    footer(),
  ],
  journal_report: () => [
    header('Journal Report'),
    dataTable('Journal Entries', [
      { key: 'date', label: 'Date', show: true, align: 'left', format: 'date' },
      { key: 'journal_no', label: 'Journal #', show: true, align: 'left', format: 'text' },
      { key: 'account', label: 'Account', show: true, align: 'left', format: 'text' },
      { key: 'description', label: 'Description', show: true, align: 'left', format: 'text' },
      { key: 'debit', label: 'Debit', show: true, align: 'right', format: 'currency' },
      { key: 'credit', label: 'Credit', show: true, align: 'right', format: 'currency' },
    ]),
    footer(),
  ],
  sales_by_customer: () => [
    header('Sales by Customer'),
    kpiCards([
      { label: 'Total Sales', valueTag: '{{ total_sales }}' },
      { label: 'Customers', valueTag: '{{ customer_count }}' },
    ]),
    dataTable('Sales by Customer', [
      { key: 'customer', label: 'Customer', show: true, align: 'left', format: 'text' },
      { key: 'invoices', label: 'Invoices', show: true, align: 'center', format: 'number' },
      { key: 'amount', label: 'Amount', show: true, align: 'right', format: 'currency' },
      { key: 'percentage', label: '%', show: true, align: 'right', format: 'percent' },
    ]),
    summaryRow('Total'),
    footer(),
  ],
  sales_by_product: () => [
    header('Sales by Product'),
    dataTable('Sales by Product', [
      { key: 'product', label: 'Product', show: true, align: 'left', format: 'text' },
      { key: 'quantity', label: 'Qty Sold', show: true, align: 'center', format: 'number' },
      { key: 'amount', label: 'Revenue', show: true, align: 'right', format: 'currency' },
      { key: 'percentage', label: '%', show: true, align: 'right', format: 'percent' },
    ]),
    summaryRow('Total'),
    footer(),
  ],
  purchases_by_supplier: () => [
    header('Purchases by Supplier'),
    dataTable('Purchases by Supplier', [
      { key: 'supplier', label: 'Supplier', show: true, align: 'left', format: 'text' },
      { key: 'bills', label: 'Bills', show: true, align: 'center', format: 'number' },
      { key: 'amount', label: 'Amount', show: true, align: 'right', format: 'currency' },
    ]),
    summaryRow('Total'),
    footer(),
  ],
  expense_by_category: () => [
    header('Expenses by Category'),
    kpiCards([
      { label: 'Total Expenses', valueTag: '{{ total_expenses }}' },
      { label: 'Categories', valueTag: '{{ category_count }}' },
    ]),
    dataTable('Expenses by Category', [
      { key: 'category', label: 'Category', show: true, align: 'left', format: 'text' },
      { key: 'amount', label: 'Amount', show: true, align: 'right', format: 'currency' },
      { key: 'percentage', label: '%', show: true, align: 'right', format: 'percent' },
    ]),
    summaryRow('Total'),
    footer(),
  ],
  invoice_summary: () => [
    header('Invoice Summary'),
    kpiCards([
      { label: 'Total Invoiced', valueTag: '{{ total_invoiced }}' },
      { label: 'Paid', valueTag: '{{ total_paid }}' },
      { label: 'Outstanding', valueTag: '{{ total_outstanding }}' },
    ]),
    dataTable('Invoice Summary', [
      { key: 'invoice_no', label: 'Invoice #', show: true, align: 'left', format: 'text' },
      { key: 'customer', label: 'Customer', show: true, align: 'left', format: 'text' },
      { key: 'date', label: 'Date', show: true, align: 'left', format: 'date' },
      { key: 'amount', label: 'Amount', show: true, align: 'right', format: 'currency' },
      { key: 'paid', label: 'Paid', show: true, align: 'right', format: 'currency' },
      { key: 'status', label: 'Status', show: true, align: 'center', format: 'text' },
    ]),
    summaryRow('Totals'),
    footer(),
  ],
  bill_summary: () => [
    header('Bill Summary'),
    kpiCards([
      { label: 'Total Bills', valueTag: '{{ total_billed }}' },
      { label: 'Paid', valueTag: '{{ total_paid }}' },
      { label: 'Outstanding', valueTag: '{{ total_outstanding }}' },
    ]),
    dataTable('Bill Summary', [
      { key: 'bill_no', label: 'Bill #', show: true, align: 'left', format: 'text' },
      { key: 'supplier', label: 'Supplier', show: true, align: 'left', format: 'text' },
      { key: 'date', label: 'Date', show: true, align: 'left', format: 'date' },
      { key: 'amount', label: 'Amount', show: true, align: 'right', format: 'currency' },
      { key: 'paid', label: 'Paid', show: true, align: 'right', format: 'currency' },
      { key: 'status', label: 'Status', show: true, align: 'center', format: 'text' },
    ]),
    summaryRow('Totals'),
    footer(),
  ],
  payment_reminders_sent: () => [
    header('Payment Reminders Sent'),
    kpiCards([
      { label: 'Total', valueTag: '{{ total_reminders }}' },
      { label: 'Sent', valueTag: '{{ sent }}' },
      { label: 'Failed', valueTag: '{{ failed }}' },
      { label: 'Pending', valueTag: '{{ pending }}' },
      { label: 'Immediate', valueTag: '{{ immediate_reminders }}' },
      { label: 'Scheduled', valueTag: '{{ scheduled_reminders }}' },
    ]),
    dataTable('Reminder Activity', [
      { key: 'date', label: 'Date', show: true, align: 'left', format: 'date' },
      { key: 'type', label: 'Type', show: true, align: 'left', format: 'text' },
      { key: 'status', label: 'Status', show: true, align: 'left', format: 'text' },
      { key: 'contact_name', label: 'Contact', show: true, align: 'left', format: 'text' },
      { key: 'schedule_name', label: 'Schedule', show: true, align: 'left', format: 'text' },
      { key: 'recipients', label: 'Recipients', show: true, align: 'left', format: 'text' },
      { key: 'sent_by', label: 'Sent By', show: true, align: 'left', format: 'text' },
      { key: 'error', label: 'Error', show: true, align: 'left', format: 'text' },
    ]),
    footer(),
  ],
  inventory_valuation: () => [
    header('Inventory Valuation'),
    kpiCards([
      { label: 'Products', valueTag: '{{ total_products }}' },
      { label: 'Quantity', valueTag: '{{ total_quantity }}' },
      { label: 'Inventory Value', valueTag: '{{ total_value }}' },
      { label: 'Average Cost', valueTag: '{{ average_cost }}' },
    ]),
    dataTable('Inventory Valuation', [
      { key: 'product_name', label: 'Product', show: true, align: 'left', format: 'text' },
      { key: 'warehouse_name', label: 'Warehouse', show: true, align: 'left', format: 'text' },
      { key: 'quantity', label: 'Quantity', show: true, align: 'right', format: 'number' },
      { key: 'cost_price', label: 'Cost Price', show: true, align: 'right', format: 'currency' },
      { key: 'sale_price', label: 'Sale Price', show: true, align: 'right', format: 'currency' },
      { key: 'stock_value', label: 'Stock Value', show: true, align: 'right', format: 'currency' },
    ]),
    summaryRow('Total Inventory Value'),
    footer(),
  ],
  stock_movement: () => [
    header('Stock Movement Report'),
    kpiCards([
      { label: 'Total In', valueTag: '{{ total_in }}' },
      { label: 'Total Out', valueTag: '{{ total_out }}' },
      { label: 'Net Movement', valueTag: '{{ net_movement }}' },
      { label: 'Transactions', valueTag: '{{ transaction_count }}' },
    ]),
    dataTable('Stock Movement', [
      { key: 'date', label: 'Date', show: true, align: 'left', format: 'date' },
      { key: 'product_name', label: 'Product', show: true, align: 'left', format: 'text' },
      { key: 'warehouse_name', label: 'Warehouse', show: true, align: 'left', format: 'text' },
      { key: 'movement_type', label: 'Movement Type', show: true, align: 'left', format: 'text' },
      { key: 'quantity', label: 'Quantity', show: true, align: 'right', format: 'number' },
    ]),
    footer(),
  ],
  low_stock_report: () => [
    header('Low Stock Alert Report'),
    kpiCards([
      { label: 'Flagged Items', valueTag: '{{ total_items }}' },
      { label: 'Low Stock', valueTag: '{{ low_stock_count }}' },
      { label: 'Out of Stock', valueTag: '{{ out_of_stock_count }}' },
      { label: 'Reorder Value', valueTag: '{{ total_reorder_value }}' },
    ]),
    dataTable('Low Stock Items', [
      { key: 'product_name', label: 'Product', show: true, align: 'left', format: 'text' },
      { key: 'warehouse_name', label: 'Warehouse', show: true, align: 'left', format: 'text' },
      { key: 'quantity_on_hand', label: 'On Hand', show: true, align: 'right', format: 'number' },
      { key: 'quantity_available', label: 'Available', show: true, align: 'right', format: 'number' },
      { key: 'reorder_level', label: 'Reorder Level', show: true, align: 'right', format: 'number' },
      { key: 'reorder_quantity', label: 'Reorder Qty', show: true, align: 'right', format: 'number' },
      { key: 'status', label: 'Status', show: true, align: 'left', format: 'text' },
    ]),
    footer(),
  ],
  warehouse_summary: () => [
    header('Warehouse Summary'),
    kpiCards([
      { label: 'Warehouses', valueTag: '{{ warehouse_count }}' },
      { label: 'Tracked Products', valueTag: '{{ product_count }}' },
      { label: 'Inventory Value', valueTag: '{{ total_value }}' },
      { label: 'Risk Lines', valueTag: '{{ risk_count }}' },
    ]),
    dataTable('Warehouse Summary', [
      { key: 'name', label: 'Warehouse', show: true, align: 'left', format: 'text' },
      { key: 'code', label: 'Code', show: true, align: 'left', format: 'text' },
      { key: 'warehouse_type', label: 'Type', show: true, align: 'left', format: 'text' },
      { key: 'product_count', label: 'Products', show: true, align: 'right', format: 'number' },
      { key: 'total_quantity', label: 'Quantity', show: true, align: 'right', format: 'number' },
      { key: 'total_value', label: 'Value', show: true, align: 'right', format: 'currency' },
      { key: 'low_stock_count', label: 'Low Stock', show: true, align: 'right', format: 'number' },
      { key: 'out_of_stock_count', label: 'Out of Stock', show: true, align: 'right', format: 'number' },
    ]),
    summaryRow('Totals'),
    footer(),
  ],
  product_profitability: () => [
    header('Product Profitability'),
    kpiCards([
      { label: 'Products', valueTag: '{{ total_products }}' },
      { label: 'Revenue', valueTag: '{{ total_revenue }}' },
      { label: 'Cost', valueTag: '{{ total_cost }}' },
      { label: 'Profit', valueTag: '{{ total_profit }}' },
    ]),
    dataTable('Product Profitability', [
      { key: 'item_name', label: 'Product', show: true, align: 'left', format: 'text' },
      { key: 'quantity_sold', label: 'Qty Sold', show: true, align: 'right', format: 'number' },
      { key: 'revenue', label: 'Revenue', show: true, align: 'right', format: 'currency' },
      { key: 'cost', label: 'Cost', show: true, align: 'right', format: 'currency' },
      { key: 'profit', label: 'Profit', show: true, align: 'right', format: 'currency' },
      { key: 'margin_percentage', label: 'Margin %', show: true, align: 'right', format: 'percent' },
    ]),
    summaryRow('Totals'),
    footer(),
  ],
  bank_reconciliation: () => [
    header('Bank Reconciliation Activity'),
    kpiCards([
      { label: 'Transactions', valueTag: '{{ transaction_count }}' },
      { label: 'Unmatched', valueTag: '{{ unmatched_count }}' },
      { label: 'Matched', valueTag: '{{ matched_count }}' },
      { label: 'Open suggestions', valueTag: '{{ open_suggestions }}' },
      { label: 'Active rules', valueTag: '{{ active_rules }}' },
    ]),
    dataTable('By bank account', [
      { key: 'bank_account_name', label: 'Account', show: true, align: 'left', format: 'text' },
      { key: 'unmatched_count', label: 'Unmatched', show: true, align: 'right', format: 'number' },
      { key: 'matched_count', label: 'Matched', show: true, align: 'right', format: 'number' },
      { key: 'excluded_count', label: 'Excluded', show: true, align: 'right', format: 'number' },
    ]),
    dataTable('Transactions', [
      { key: 'transaction_date', label: 'Date', show: true, align: 'left', format: 'date' },
      { key: 'bank_account_name', label: 'Bank account', show: true, align: 'left', format: 'text' },
      { key: 'description', label: 'Description', show: true, align: 'left', format: 'text' },
      { key: 'amount', label: 'Amount', show: true, align: 'right', format: 'currency' },
      { key: 'reconciliation_status', label: 'Status', show: true, align: 'center', format: 'text' },
      { key: 'matched_ref', label: 'Matched to', show: true, align: 'left', format: 'text' },
    ]),
    footer(),
  ],
  inventory_order_pipeline: () => [
    header('Inventory Orders & Work Orders'),
    kpiCards([
      { label: 'Documents', valueTag: '{{ document_count }}' },
      { label: 'Line items', valueTag: '{{ total_lines }}' },
    ]),
    dataTable('Orders & work orders', [
      { key: 'document_date', label: 'Date', show: true, align: 'left', format: 'date' },
      { key: 'item_type', label: 'Type', show: true, align: 'left', format: 'text' },
      { key: 'item_number', label: 'Number', show: true, align: 'left', format: 'text' },
      { key: 'title', label: 'Title', show: true, align: 'left', format: 'text' },
      { key: 'contact_name', label: 'Contact', show: true, align: 'left', format: 'text' },
      { key: 'status', label: 'Status', show: true, align: 'center', format: 'text' },
      { key: 'grand_total', label: 'Total', show: true, align: 'right', format: 'currency' },
      { key: 'line_count', label: 'Lines', show: true, align: 'right', format: 'number' },
    ]),
    footer(),
  ],
};

const HR_DEFAULTS: Record<string, SectionFactory> = {
  payslip: () => [
    header('Payslip'), companyInfo(),
    sectionFields('employee_details', 'Employee Details'),
    sectionFields('pay_period', 'Pay Period'),
    kpiCards(),
    dataTable('Earnings', [
      { key: 'description', label: 'Description', show: true, align: 'left', format: 'text' },
      { key: 'amount', label: 'Amount', show: true, align: 'right', format: 'currency' },
    ], 'earnings'),
    dataTable('Deductions', [
      { key: 'description', label: 'Description', show: true, align: 'left', format: 'text' },
      { key: 'amount', label: 'Amount', show: true, align: 'right', format: 'currency' },
    ], 'deductions'),
    dataTable('Employer Contributions', [
      { key: 'description', label: 'Description', show: true, align: 'left', format: 'text' },
      { key: 'amount', label: 'Amount', show: true, align: 'right', format: 'currency' },
    ], 'company_contributions'),
    dataTable('Leave Balances', [
      { key: 'type', label: 'Leave Type', show: true, align: 'left', format: 'text' },
      { key: 'balance', label: 'Balance (Days)', show: true, align: 'center', format: 'text' },
      { key: 'used', label: 'Used (Days)', show: true, align: 'center', format: 'text' },
    ], 'leave_balances'),
    sectionFields('ytd', 'Year-to-Date Totals'),
    sectionFields('deferral_notice', 'Deductions Deferred to Employee Loan'),
    bankDetails(),
    footer(),
  ],
  employment_contract: () => [
    header('Employment Contract'), companyInfo(),
    metaInfo(['employee', 'position', 'contract_type', 'status']),
    sectionFields('employment_details'),
    sectionFields('dates_duration'),
    sectionFields('compensation'),
    sectionFields('compliance'),
    termsNotes(),
    signatureBlock('Signatures'),
    footer(),
  ],
  legal_contract: () => [
    header('Legal Contract'), companyInfo(),
    metaInfo(['status', 'generated_by', 'date', 'jurisdiction']),
    sectionFields('parties'),
    termsNotes(),
    signatureBlock('Signatures'),
    footer(),
  ],
  leave_report: () => [
    header('Leave Report'),
    metaInfo(['employee_name', 'period', 'leave_balance']),
    dataTable('Leave Entries', [
      { key: 'type', label: 'Type', show: true, align: 'left', format: 'text' },
      { key: 'start_date', label: 'From', show: true, align: 'left', format: 'date' },
      { key: 'end_date', label: 'To', show: true, align: 'left', format: 'date' },
      { key: 'days', label: 'Days', show: true, align: 'center', format: 'number' },
      { key: 'status', label: 'Status', show: true, align: 'center', format: 'text' },
    ]),
    footer(),
  ],
  payroll_summary: () => [
    header('Payroll Summary'),
    companyInfo(),
    sectionFields('run_details', 'Payroll Run Details'),
    kpiCards(),
    dataTable('Payroll Summary'),
    sectionFields('statutory_totals', 'Statutory Deductions Summary'),
    footer(),
  ],
  vacancy_advert: () => [
    header('Vacancy Advert'),
    companyInfo(),
    kpiCards(),
    sectionFields('vacancy_details', 'Position Details'),
    sectionFields('description', 'About the Role'),
    sectionFields('responsibilities', 'Key Responsibilities'),
    sectionFields('requirements', 'Requirements'),
    sectionFields('how_to_apply', 'How to Apply'),
    footer(),
  ],
  candidate_cv: () => [
    header('Candidate CV'),
    metaInfo(),
    // No sectionKey — renders every parsed CV section in document order
    // using the free-flowing 'cv' layout (paragraphs, bullets, entry dates).
    section('section_fields', 'Curriculum Vitae', { renderMode: 'cv' }),
    footer(),
  ],
};

// ============================================================================
// PUBLIC API
// ============================================================================

/** Lookup table combining all category defaults */
const ALL_DEFAULTS: Record<string, SectionFactory> = {
  ...FINANCE_DOCUMENT_DEFAULTS,
  ...FORM_DEFAULTS,
  ...OPS_DEFAULTS,
  ...VOTING_DEFAULTS,
  ...FINANCE_REPORT_DEFAULTS,
  ...HR_DEFAULTS,
};

/**
 * Get the default section list for a given document type.
 * Returns a fresh copy every time (new IDs).
 */
export function getDefaultSections(docType: DocumentType): UnifiedTemplateSection[] {
  const factory = ALL_DEFAULTS[docType];
  if (factory) {
    return factory();
  }
  // Fallback: minimal template
  const category = DOCUMENT_TYPE_CATEGORY[docType];
  switch (category) {
    case 'finance_document':
      return [header('Document'), companyInfo(), documentDetails(), lineItems(), totals(), footer()];
    case 'finance_report':
      return [header('Report'), dataTable('Data', []), footer()];
    case 'operations_card':
      return [header('Card'), contactInfo(), metaInfo(), footer()];
    case 'form_submission':
      return [header('Form'), fieldsTable(), footer()];
    case 'hr_document':
      return [header('Document'), companyInfo(), footer()];
    case 'meeting_page':
      return [
        header(DOCUMENT_TYPE_LABELS[docType] || 'Meeting Page'),
        section('custom_text', 'Hero Copy', {
          content: '{{ meeting_title }}\n\n{{ lobby_message }}',
          textSize: 'lg',
          align: 'center',
        }),
        section('meta_info', 'Meeting Details', {
          fields: ['meeting_title', 'host_name', 'starts_in', 'meeting_status'],
          layout: 'cards',
        }),
        footer(),
      ];
    case 'user_guide':
      // Fallback only — the userGuideAdapter regenerates template.sections per
      // document to preserve inline block order (headings → tables → steps).
      return [
        header('User Guide'),
        section('section_fields', 'Guide content', { renderMode: 'cv' }),
        dataTable('Tables'),
        checklist(),
        footer(),
      ];
    default:
      return [header('Document'), footer()];
  }
}

/**
 * Get only the default table columns for a given document type.
 * Used to pre-populate the table_columns field.
 */
export function getDefaultTableColumns(docType: DocumentType): TemplateTableColumn[] {
  const category = DOCUMENT_TYPE_CATEGORY[docType];
  if (category === 'finance_document') {
    if (['delivery_note', 'waybill', 'grn', 'rma'].includes(docType)) return [...DELIVERY_NOTE_COLUMNS];
    if (['picking_slip', 'packing_slip', 'stock_adjustment', 'transfer_order'].includes(docType)) {
      return [...WAREHOUSE_MOVEMENT_COLUMNS];
    }
    return [...DEFAULT_LINE_ITEM_COLUMNS];
  }
  return [];
}
